import {
  createBrowserErrorCallbacks,
  createBrowserErrorStorage,
  createErrorHandler,
  RU_ERROR_MESSAGE_OVERRIDES,
  type ApiError,
} from '@crowdfpl/api-client';
import * as Sentry from '@sentry/react';

export type { ApiError };

const isAnalyticsEnabled =
  import.meta.env.PROD || import.meta.env.VITE_ENABLE_ANALYTICS === 'true';

/** Expected client-challenge / policy codes — not product defects. */
const EXPECTED_API_ERROR_CODES = new Set([
  'REVOTE_LIMIT_REACHED',
  'REVOTE_FINAL5_LIMIT',
  'REVOTE_COOLDOWN_ACTIVE',
  'REVOTE_CONFIRM_REQUIRED',
  'UNVOTE_NOT_SUPPORTED',
]);

const reportToSentry = (apiError: ApiError): void => {
  if (!isAnalyticsEnabled) {
    return;
  }

  if (apiError.code && EXPECTED_API_ERROR_CODES.has(apiError.code)) {
    return;
  }

  try {
    const error = new Error(apiError.message);
    error.name = `API Error ${apiError.status ?? 'Unknown'}`;

    Sentry.captureException(error, {
      tags: {
        error_type: 'api_error',
        error_context: apiError.context ?? 'unknown',
        is_auth_error: apiError.isAuthError ? 'true' : 'false',
      },
      extra: {
        error_message: apiError.message,
        error_status: apiError.status,
        error_code: apiError.code,
        error_context: apiError.context,
        is_auth_error: apiError.isAuthError,
        timestamp: apiError.timestamp,
      },
      level: apiError.status && apiError.status >= 500 ? 'error' : 'warning',
    });
  } catch (sentryError) {
    console.warn('Не удалось отправить ошибку в Sentry:', sentryError);
  }
};

const coreHandler = createErrorHandler({
  overrides: RU_ERROR_MESSAGE_OVERRIDES,
  storage: createBrowserErrorStorage(),
  callbacks: createBrowserErrorCallbacks(),
});

const handleApiError = (error: unknown, context: string = 'Unknown'): ApiError => {
  const apiError = coreHandler.handleApiError(error, context);
  reportToSentry(apiError);
  return apiError;
};

export const errorHandler = {
  handleApiError,
  getErrorLog: coreHandler.getErrorLog,
  getStoredErrors: coreHandler.getStoredErrors,
  clearErrorLog: coreHandler.clearErrorLog,
  hasCriticalErrors: coreHandler.hasCriticalErrors,
};

export const handleError = (error: unknown, context?: string) =>
  errorHandler.handleApiError(error, context);

export const logError = (message: string, context?: string) =>
  errorHandler.handleApiError(new Error(message), context);
